import React from 'react';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { useIntl } from 'react-intl';
import { Flex } from '@osmosis/tailwind-ui/components/Box';

interface DashboardSearchTipProps {
  icon: any;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
  dataTestId?: string;
  type?: string;
}

/**
 * DashboardSearchTip component displays a search tip with an icon and localized text.
 * @param {DashboardSearchTipProps} props - The properties for the component.
 * @param {any} [props.icon] - The icon to display.
 * @param {string} [props.iconClassName] - Optional class name for the icon to apply additional styles.
 * @param {React.CSSProperties} [props.iconStyle] - Optional inline styles for the icon.
 * @param {string} [props.dataTestId] - Optional data-testid for testing purposes to identify the component in tests.
 * @param {string} [props.type] - The type of search tip to display,
 * which determines the localized text shown.
 * @returns {JSX.Element} The rendered component.
 */
const DashboardSearchTip: React.FC<DashboardSearchTipProps> = ({
  icon,
  iconClassName,
  dataTestId,
  type,
}: DashboardSearchTipProps): JSX.Element => {
  const intl = useIntl();

  const searchTipsTypesLocalized = {
    renal: intl.formatMessage({
      id: 'searchTipsTypesLocalized.renal',
      defaultMessage: 'Try: "Renal Physiology"',
    }),
    aminoAcids: intl.formatMessage({
      id: 'searchTipsTypesLocalized.aminoAcids',
      defaultMessage: '"Amino Acids"',
    }),
    pharmacokinetics: intl.formatMessage({
      id: 'searchTipsTypesLocalized.pharmacokinetics',
      defaultMessage: '"Pharmacokinetics"...',
    }),
  };

  return (
    <Flex
      className="items-center justify-between gap-1"
      data-testid="search-tip"
    >
      <Icon icon={icon} className={iconClassName} data-testid={dataTestId} />
      <P className="italic">
        {
          searchTipsTypesLocalized[
            type as keyof typeof searchTipsTypesLocalized
          ]
        }
      </P>
    </Flex>
  );
};

export default DashboardSearchTip;
